import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * "BUY NOW" (blocks/ssc-buy-now.liquid): adds the selected variant to the cart and goes to checkout.
 * - The variant ID and quantity come from the theme's product form (blocks/buy-buttons.liquid) in the
 *   same product details at click time, so they always match what ADD TO CART would submit.
 * - Follows the product select event (as assets/ssc-notify-me.js) to disable the button while the
 *   selected variant is sold out. Hiding next to notify me / the waitlist is pure CSS.
 * - Loading: aria-busy + disabled; a failure shows the error text and the button can be pressed again.
 *
 * @typedef {{ button: HTMLButtonElement, error: HTMLElement }} Refs
 * @extends {Component<Refs>}
 */
class SscBuyNow extends Component {
  requiredRefs = ['button', 'error'];

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {AbortController | undefined} */
  #requestController;

  /** @type {boolean} */
  #busy = false;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.closest('.shopify-section, dialog')?.addEventListener(StandardEvents.productSelect, this.handleProductSelect, {
      signal,
    });
    // Back from checkout restores the page from the bfcache with the button still busy
    window.addEventListener(
      'pageshow',
      (event) => {
        if (event.persisted) this.#setBusy(false);
      },
      { signal }
    );

    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = undefined;
    this.#requestController?.abort();
    this.#requestController = undefined;
  }

  /**
   * A mixed sock pack (blocks/ssc-pack-selector.liquid) adds several variants at once
   * @returns {{ id: number, quantity: number }[] | null}
   */
  #packItems() {
    const selector = this.closest('.product-details')?.querySelector('ssc-pack-selector');
    return selector && 'mixedItems' in selector && typeof selector.mixedItems === 'function' ? selector.mixedItems() : null;
  }

  /** @returns {HTMLFormElement | null} */
  get #productForm() {
    const scope = this.closest('.product-details') ?? this.closest('.shopify-section, dialog');
    const form = scope?.querySelector('product-form-component form[data-type="add-to-cart-form"]');
    return form instanceof HTMLFormElement ? form : null;
  }

  #render() {
    const available = this.dataset.available === 'true';
    this.refs.button.disabled = !available || this.#busy;
  }

  /** @param {boolean} on */
  #setBusy(on) {
    this.#busy = on;
    const { button } = this.refs;
    if (on) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
    this.#render();
  }

  /** @param {string} message */
  #setError(message) {
    this.refs.error.textContent = message;
  }

  /**
   * @param {ProductSelectEvent} event - The product select event.
   */
  handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail || !this.isConnected) return;

        const { newProduct, productId, resource } = detail;
        if (newProduct) {
          this.dataset.productId = String(newProduct.id);
        } else if (productId && String(productId) !== this.dataset.productId) {
          return;
        }

        this.dataset.available = String(Boolean(resource?.available));
        this.#setError('');
        this.#render();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[ssc-buy-now] Event promise rejected:', error);
      });
  };

  async buyNow() {
    if (this.#busy || this.dataset.available !== 'true') return;

    const form = this.#productForm;
    if (!form) return;
    const formData = new FormData(form);
    const id = Number(formData.get('id'));
    if (!id) return;
    const quantity = Math.max(1, Number(formData.get('quantity')) || 1);

    const root = window.Shopify?.routes?.root ?? '/';
    this.#setError('');
    this.#setBusy(true);
    this.#requestController?.abort();
    this.#requestController = new AbortController();

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: this.#packItems() ?? [{ id, quantity }] }),
        signal: this.#requestController.signal,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status) {
        this.#setError(data.description || this.dataset.labelError || '');
        this.#setBusy(false);
        return;
      }
      window.location.href = `${root}checkout`;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error(error);
      this.#setError(this.dataset.labelError ?? '');
      this.#setBusy(false);
    }
  }
}

if (!customElements.get('ssc-buy-now')) {
  customElements.define('ssc-buy-now', SscBuyNow);
}
